import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSpaceStore } from '../stores/spaceStore';
import { useCategoryStore } from '../stores/categoryStore';
import { useExpenseStore } from '../stores/expenseStore';
import { useAuthStore } from '../stores/authStore';
import BottomSheet from '../components/ui/BottomSheet';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { useLongPress } from '../hooks/useLongPress';
import { TOAST, SPLIT_OPTIONS, CATEGORY_EMOJIS, MAX_LENGTHS } from 'pairtab-shared/constants';
import MemberChecklistSheet from '../components/MemberChecklistSheet';
import ProUpgradeModal from '../components/ProUpgradeModal';

/* ─── Extracted to avoid re-mounting useLongPress hooks on parent re-render ─── */
interface CatTileProps {
  cat: {
    id: string;
    name: string;
    emoji: string;
    is_fixed: boolean;
    is_pinned: boolean;
    is_hidden: boolean;
    fixed_configs?: { amount_cents: number; due_day: number; autopay: boolean; is_paused: boolean }[];
    category_defaults?: { action: 'split' | 'keep'; split_ratio: number }[];
  };
  defaultPref: { action: 'split' | 'keep'; split_ratio: number } | null;
  isPrefilledAmount: boolean;
  onTap: (id: string) => void;
  onLongPress: (id: string) => void;
}

const CatTile = memo(function CatTile({ cat, defaultPref, isPrefilledAmount, onTap, onLongPress }: CatTileProps) {
  const pressProps = useLongPress({
    onLongPress: () => onLongPress(cat.id),
    onClick: () => onTap(cat.id),
    duration: 500,
  });

  return (
    <div
      {...pressProps}
      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border active:scale-95 transition-transform touch-target cursor-pointer select-none ${
        cat.is_fixed
          ? 'bg-primary/10 border-primary/30'
          : 'bg-card border-border'
      }`}
    >
      <span className="text-2xl leading-none">{cat.emoji}</span>
      <span className="text-xs font-medium truncate w-full text-center">{cat.name}</span>
      {cat.is_fixed && (
        <div className="flex items-center gap-1">
          <Badge variant="fixed" size="sm">FIXED</Badge>
          {isPrefilledAmount && <Lock size={10} className="text-primary/60" />}
        </div>
      )}
      {defaultPref && !cat.is_fixed && (
        <span className="text-[10px] text-muted">
          {defaultPref.action === 'keep' ? '🔒 Keep' : `${defaultPref.split_ratio}/${100 - defaultPref.split_ratio}`}
        </span>
      )}
    </div>
  );
});
/* ─── End CatTile ─── */

const formatCents = (val: string) => Math.round(parseFloat(val) * 100) || 0;
const formatDollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function LogExpense() {
  const navigate = useNavigate();
  const { currentSpace, members, fetchMembers } = useSpaceStore();
  const { categories, fetchCategories, updateCategoryDefault, getCategoryDefault, createCategoryWithConfig } = useCategoryStore();
  const { createExpense, prefill, setPrefill } = useExpenseStore();
  const { isPro, user } = useAuthStore();

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [payerId, setPayerId] = useState('');
  const [showOverrideSheet, setShowOverrideSheet] = useState(false);
  const [overrideCategory, setOverrideCategory] = useState<string | null>(null);
  const [showCustomRatio, setShowCustomRatio] = useState(false);
  const [customA, setCustomA] = useState(50);
  const [showChecklistSheet, setShowChecklistSheet] = useState(false);
  const [checklistCategory, setChecklistCategory] = useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [showAddCategorySheet, setShowAddCategorySheet] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [addCatEmoji, setAddCatEmoji] = useState('📦');
  const [addCatName, setAddCatName] = useState('');
  const [addCatIsFixed, setAddCatIsFixed] = useState(false);
  const [addCatFixedAmount, setAddCatFixedAmount] = useState('');
  const [addCatDueDay, setAddCatDueDay] = useState('1');
  const [addCatAction, setAddCatAction] = useState<'split' | 'keep'>('split');
  const [addCatRatio, setAddCatRatio] = useState(50);
  const logQueue = useRef<Set<string>>(new Set());
  const [amountError, setAmountError] = useState(false);
  const currentMemberId = useMemo(
    () => members.find((member) => member.user_id === user?.id)?.id || '',
    [members, user?.id]
  );

  useEffect(() => {
    if (currentSpace?.id) {
      void Promise.all([
        fetchCategories(currentSpace.id),
        fetchMembers(currentSpace.id),
      ]);
    }
  }, [currentSpace?.id, fetchCategories, fetchMembers]);

  useEffect(() => {
    if (members.length === 0) {
      return;
    }

    const defaultPayerId = currentMemberId || members[0]?.id || '';
    const payerStillExists = members.some((member) => member.id === payerId);

    if (!payerStillExists || !payerId) {
      setPayerId(defaultPayerId);
    }
  }, [members, currentMemberId, payerId]);

  useEffect(() => {
    if (prefill) {
      if (prefill.amountCents) setAmount((prefill.amountCents / 100).toString());
      if (prefill.categoryId) {
        const cat = categories.find(c => c.id === prefill.categoryId);
        if (cat && cat.is_fixed && cat.fixed_configs?.[0]) {
          setAmount((cat.fixed_configs[0].amount_cents / 100).toString());
        }
      }
      setPrefill(null);
    }
  }, [prefill, categories]);

  const handleCategoryTap = useCallback((categoryId: string) => {
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return;

    // Auto-fill amount from fixed config if the input is empty
    if (cat.is_fixed && cat.fixed_configs?.[0] && (!amount || parseFloat(amount) <= 0)) {
      const fixedDollars = (cat.fixed_configs[0].amount_cents / 100).toString();
      setAmount(fixedDollars);
      setAmountError(false);
      // Don't return — continue to log with the auto-filled amount
      logExpense(categoryId, undefined, undefined, undefined, cat.fixed_configs[0].amount_cents);
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setAmountError(true);
      toast.error(TOAST.errorAmountRequired);
      return;
    }
    setAmountError(false);

    if (members.length >= 3 && cat.is_fixed === false) {
      setChecklistCategory(categoryId);
      setSelectedMembers(members.map(m => m.id));
      setShowChecklistSheet(true);
      return;
    }

    logExpense(categoryId);
  }, [amount, categories, members]);

  const logExpense = useCallback(async (categoryId: string, action?: 'split' | 'keep', splitRatio?: number, excludedMemberIds?: string[], overrideAmountCents?: number) => {
    const amountCents = overrideAmountCents || formatCents(amount);
    if (amountCents <= 0) {
      toast.error(TOAST.errorAmountRequired);
      return;
    }
    if (!currentMemberId) {
      toast.error('Could not identify your member profile');
      return;
    }
    if (logQueue.current.has(categoryId)) return;
    logQueue.current.add(categoryId);

    const def = getCategoryDefault(categoryId);
    const finalAction = action || def?.action || 'split';
    const finalRatio = splitRatio !== undefined ? splitRatio : def?.split_ratio ?? 50;

    try {
      await createExpense({
        space_id: currentSpace!.id,
        category_id: categoryId,
        amount_cents: amountCents,
        description,
        action: finalAction,
        split_ratio: finalRatio,
        paid_by: payerId || currentMemberId,
        excluded_members: excludedMemberIds,
      });

      // Save override as new default if user explicitly chose a split option
      if (action || splitRatio !== undefined) {
        await updateCategoryDefault(categoryId, currentSpace!.id, finalAction, finalRatio);
      }

      const displayAmount = formatDollars(amountCents);
      toast.success(finalAction === 'split'
        ? `${displayAmount} — ${finalRatio}/${100 - finalRatio} split`
        : `${displayAmount} logged`
      );
      setDescription('');
    } catch (err: any) {
      toast.error(err.message || TOAST.errorGeneric);
    } finally {
      logQueue.current.delete(categoryId);
    }
  }, [amount, currentMemberId, description, payerId, currentSpace, createExpense, updateCategoryDefault, getCategoryDefault]);

  const handleChecklistConfirm = () => {
    if (!checklistCategory) return;
    const excluded = members.filter(m => !selectedMembers.includes(m.id)).map(m => m.id);
    logExpense(checklistCategory, undefined, undefined, excluded.length > 0 ? excluded : undefined);
    setShowChecklistSheet(false);
    setChecklistCategory(null);
  };

  const handleLongPressStart = useCallback((categoryId: string) => {
    setOverrideCategory(categoryId);
    setShowOverrideSheet(true);
    setShowCustomRatio(false);
  }, []);

  const handleSplitSelect = useCallback((val: number) => {
    if (!overrideCategory) return;
    if (val === -1) {
      setShowCustomRatio(true);
      return;
    }
    const action = val === 0 ? 'keep' : 'split';
    const ratio = val;
    logExpense(overrideCategory, action, ratio);
    setShowOverrideSheet(false);
    setOverrideCategory(null);
    setShowCustomRatio(false);
  }, [overrideCategory, logExpense]);

  const handleCustomRatioSave = () => {
    if (!overrideCategory) return;
    if (customA < 0 || customA > 100) return;
    const ratio = customA;
    logExpense(overrideCategory, 'split', ratio);
    setShowOverrideSheet(false);
    setOverrideCategory(null);
    setShowCustomRatio(false);
  };

  const handleAddCategory = async () => {
    if (!currentSpace?.id) {
      toast.error('No active space selected');
      return;
    }

    if (!addCatName.trim()) {
      toast.error(TOAST.errorCategoryName);
      return;
    }
    if (addCatName.length > MAX_LENGTHS.categoryName) {
      toast.error(TOAST.errorCategoryNameMax);
      return;
    }

    const customCount = categories.filter(c => c.is_custom).length;
    if (!isPro && customCount >= 3) {
      setShowUpgradeModal(true);
      return;
    }

    const fixedAmountCents = formatCents(addCatFixedAmount);
    const dueDay = parseInt(addCatDueDay, 10) || 1;

    if (addCatIsFixed && fixedAmountCents <= 0) {
      toast.error('Enter a fixed amount greater than 0');
      return;
    }

    try {
      await createCategoryWithConfig(
        {
          space_id: currentSpace.id,
          name: addCatName.trim(),
          emoji: addCatEmoji,
          is_fixed: addCatIsFixed,
          is_custom: true,
        },
        addCatIsFixed
          ? {
              amount_cents: fixedAmountCents,
              due_day: dueDay,
              autopay: false,
              is_paused: false,
            }
          : undefined,
        { action: addCatAction, split_ratio: addCatRatio }
      );

      toast.success(TOAST.categoryAdded);
      setShowAddCategorySheet(false);
      setAddCatName('');
      setAddCatEmoji('📦');
      setAddCatIsFixed(false);
      setAddCatFixedAmount('');
      setAddCatDueDay('1');
      setAddCatAction('split');
      setAddCatRatio(50);
    } catch (err: any) {
      if (err.message?.includes('limited to 3')) {
        setShowUpgradeModal(true);
      } else {
        toast.error(err.message || TOAST.errorGeneric);
      }
    }
  };

  const computePerPersonAmount = (ratio: number) => {
    const cents = formatCents(amount);
    const myShare = Math.round(cents * ratio / 100);
    const theirShare = cents - myShare;
    return { myShare, theirShare };
  };

  const pinned = categories.filter(c => c.is_pinned && !c.is_hidden);
  const rest = categories.filter(c => !c.is_pinned && !c.is_hidden);

  const { myShare, theirShare } = computePerPersonAmount(showCustomRatio ? customA : (getCategoryDefault(overrideCategory || '')?.split_ratio ?? 50));

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/')} className="p-2 -ml-2 rounded-full hover:bg-muted/10 touch-target">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold">Log Expense</h1>
      </div>

      {members.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted">Who paid?</label>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {members.map(m => (
              <button
                key={m.id}
                onClick={() => setPayerId(m.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors touch-target ${
                  payerId === m.id ? 'bg-primary text-background' : 'bg-card border border-border text-foreground'
                }`}
              >
                <span>{m.avatar_emoji || '👤'}</span>
                <span className="font-medium text-sm">{m.display_name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium text-muted">Amount</label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-muted">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={e => {
              const v = e.target.value.replace(/[^0-9.]/g, '');
              if (v.split('.').length <= 2) {
                setAmount(v);
                setAmountError(false);
              }
            }}
            placeholder="0.00"
            className={`w-full bg-card border rounded-2xl py-4 pl-10 pr-4 text-3xl font-bold text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 tabular-nums ${
              amountError ? 'border-danger' : 'border-border'
            }`}
            aria-label="Amount"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-muted">Description (optional)</label>
          <span className="text-xs text-muted">{description.length}/{MAX_LENGTHS.description}</span>
        </div>
        <input
          type="text"
          value={description}
          onChange={e => {
            if (e.target.value.length <= MAX_LENGTHS.description) {
              setDescription(e.target.value);
            }
          }}
          placeholder="e.g., Grocery run"
          className="input-field"
        />
      </div>

      <div className="space-y-3">
        <label className="text-sm font-medium text-muted">Tap a category to log</label>
        {pinned.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {pinned.map(c => (
              <CatTile
                key={c.id}
                cat={c}
                defaultPref={getCategoryDefault(c.id)}
                isPrefilledAmount={!!(c.is_fixed && c.fixed_configs?.[0] && amount === (c.fixed_configs[0].amount_cents / 100).toString())}
                onTap={handleCategoryTap}
                onLongPress={handleLongPressStart}
              />
            ))}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          {rest.map(c => (
            <CatTile
              key={c.id}
              cat={c}
              defaultPref={getCategoryDefault(c.id)}
              isPrefilledAmount={!!(c.is_fixed && c.fixed_configs?.[0] && amount === (c.fixed_configs[0].amount_cents / 100).toString())}
              onTap={handleCategoryTap}
              onLongPress={handleLongPressStart}
            />
          ))}
          <button
            onClick={() => setShowAddCategorySheet(true)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-card border-2 border-dashed border-muted/40 text-muted active:scale-95 transition-transform touch-target"
          >
            <span className="text-2xl">➕</span>
            <span className="text-xs font-medium">New</span>
          </button>
        </div>
      </div>

      <BottomSheet isOpen={showOverrideSheet} onClose={() => { setShowOverrideSheet(false); setShowCustomRatio(false); }} title="Override Split">
        {!showCustomRatio ? (
          <div className="space-y-3">
            {overrideCategory && (() => {
              const def = getCategoryDefault(overrideCategory);
              return def ? (
                <p className="text-xs text-muted text-center">
                  Current default: {def.action === 'keep' ? 'KEEP' : `${def.split_ratio}/${100 - def.split_ratio} split`}
                </p>
              ) : null;
            })()}
            <div className="grid grid-cols-2 gap-3">
              {SPLIT_OPTIONS.map(o => (
                <Button key={o.label} variant="secondary" onClick={() => handleSplitSelect(o.value)}>
                  {o.label}
                </Button>
              ))}
            </div>
            <p className="text-muted text-xs mt-4 text-center">Long-press to see this again</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-xs text-muted mb-1 block">You (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={customA}
                  onChange={e => setCustomA(Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                  className="w-full bg-card border border-border rounded-xl py-3 px-4 text-center text-xl font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted mb-1 block">Them (%)</label>
                <div className="w-full bg-card border border-border rounded-xl py-3 px-4 text-center text-xl font-bold tabular-nums text-muted">
                  {100 - customA}%
                </div>
              </div>
            </div>
            <div className="text-center space-y-1 bg-muted/10 rounded-lg p-3">
              <p className="text-sm font-medium">Your share: <span className="tabular-nums">{formatDollars(myShare)}</span></p>
              <p className="text-sm font-medium">Their share: <span className="tabular-nums">{formatDollars(theirShare)}</span></p>
            </div>
            <Button onClick={handleCustomRatioSave} className="w-full py-3">Save & Log</Button>
          </div>
        )}
      </BottomSheet>

      <BottomSheet isOpen={showChecklistSheet} onClose={() => setShowChecklistSheet(false)} title="Include in Split">
        <MemberChecklistSheet
          isOpen={showChecklistSheet}
          onClose={() => setShowChecklistSheet(false)}
          members={members}
          selectedIds={selectedMembers}
          onSelectionChange={setSelectedMembers}
          amountCents={formatCents(amount)}
          isPro={isPro}
          onUpgradeAttempted={() => setShowUpgradeModal(true)}
        />
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-center text-sm text-muted mb-3">
            {selectedMembers.length} people × {formatDollars(selectedMembers.length > 0 ? Math.round(formatCents(amount) / selectedMembers.length) : 0)} each
          </div>
          <Button onClick={handleChecklistConfirm} className="w-full py-3">Log Expense</Button>
        </div>
      </BottomSheet>

      <BottomSheet isOpen={showAddCategorySheet} onClose={() => setShowAddCategorySheet(false)} title="Add Category">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-muted mb-2 block">Emoji</label>
            <div className="grid grid-cols-6 gap-2">
              {CATEGORY_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => setAddCatEmoji(emoji)}
                  className={`text-xl p-2 rounded-lg transition-colors ${
                    addCatEmoji === emoji ? 'bg-primary/20 ring-2 ring-primary' : 'bg-card'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <label className="text-sm font-medium text-muted">Name</label>
              <span className="text-xs text-muted">{addCatName.length}/{MAX_LENGTHS.categoryName}</span>
            </div>
            <input
              type="text"
              value={addCatName}
              onChange={e => {
                if (e.target.value.length <= MAX_LENGTHS.categoryName) setAddCatName(e.target.value);
              }}
              placeholder="e.g., Pet Food"
              className="input-field"
            />
          </div>

          <div className="flex items-center justify-between bg-card rounded-xl p-3">
            <span className="text-sm font-medium">Fixed expense</span>
            <button
              type="button"
              onClick={() => setAddCatIsFixed(!addCatIsFixed)}
              aria-pressed={addCatIsFixed}
              className={`relative h-7 w-12 rounded-full transition-colors ${
                addCatIsFixed ? 'bg-primary' : 'bg-muted/30'
              }`}
            >
              <span
                className={`absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-white transition-transform ${
                  addCatIsFixed ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {addCatIsFixed && (
            <div className="space-y-3 bg-muted/10 rounded-xl p-3">
              <div className="space-y-1">
                <label className="text-xs text-muted">Amount</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={addCatFixedAmount}
                  onChange={e => setAddCatFixedAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="0.00"
                  className="w-full bg-card border border-border rounded-lg py-2 px-3 tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted">Due day (1-31)</label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={addCatDueDay}
                  onChange={e => setAddCatDueDay(e.target.value)}
                  className="w-full bg-card border border-border rounded-lg py-2 px-3 tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted">Default split</label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'KEEP', action: 'keep' as const, ratio: 0 },
                { label: '50/50', action: 'split' as const, ratio: 50 },
                { label: '60/40', action: 'split' as const, ratio: 60 },
                { label: '70/30', action: 'split' as const, ratio: 70 },
              ].map(opt => (
                <button
                  key={opt.label}
                  onClick={() => { setAddCatAction(opt.action); setAddCatRatio(opt.ratio); }}
                  className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                    (addCatAction === opt.action && addCatRatio === opt.ratio)
                      ? 'bg-primary text-background'
                      : 'bg-card border border-border'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={handleAddCategory} className="w-full py-3">Add Category</Button>
        </div>
      </BottomSheet>

      <ProUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgradeMonthly={() => {}}
        onUpgradeYearly={() => {}}
      />
    </div>
  );
}
